import { Hidden } from '@mui/material';
import { Breakpoint } from '@mui/material/styles';
import React, { memo, useCallback, useState, useMemo } from 'react';
import { DraggableProvided } from 'react-beautiful-dnd';
import clsx from 'clsx';
import Button from '@ui/Button';
import Icon from '@ui/Icon';
import {
  ClipObject,
  ClipObjectPending,
  instanceOfCompletedClip,
  instanceOfFailedPendingClip,
} from '../../context/clips';
import useBreakpoint from '../../lib/useBreakpoint';
import shallowCompare from '../../util/shallowCompare';
import { CheckboxInput } from '../Inputs';
import StudioClipActions from './Actions/StudioClipActions';
import useStudioClipCompleteStyles from './States/Complete/completeStyles';
import CompleteCardState from './States/Complete';
import RenderingCardState from './States/Rendering';
import StudioClipCard from './StudioClipCard';
import { usePersistentClipStateAndActions } from '../StudioClipUserList/hooks/useStudioClips/common';
import { ClipPreview } from '../../context/previewClip/StudioPreviewClipContext';

/**
 * StudioClipContext is used to help compose the state/action components within a StudioClip. This
 * is mostly to avoid passing props all over and provide a cleaner markup.
 */
type ClipContextProps = {
  clip: StudioClipProps['clip'];
  breakpoint: Breakpoint | null;
  actionMenuAnchor: HTMLElement | null;
  audio: HTMLAudioElement | undefined;
  setActionMenuAnchor: (anchor: HTMLElement | null) => void;
  srcFailedToLoad?: boolean;
  setSrcFailedToLoad: (failed: boolean) => void;
  setAudio: (audio: HTMLAudioElement | undefined, clipId?: string) => void;
};

export const StudioClipContext = React.createContext<ClipContextProps>({
  // @ts-ignore React.createContext is expecting default state to be populated but does not make
  // sense in this scenario.
  clip: {},
  breakpoint: null,
  actionMenuAnchor: null,
  audio: undefined,
});

export interface StudioClipProps {
  clip: ClipObject | ClipObjectPending;
  previewClip?: ClipPreview;
  draggableProvided?: DraggableProvided;
  isDragging?: boolean;
  isReadOnly?: boolean;
  // (optional) omitting this property will disable the input field for the clip name
  onNameChange?: (clipName: string) => void;
  // (optional) controlled input for clip "selection". Omitting this will not display the checkbox
  selected?: boolean;
  onSelectedChange?: (
    clipId: string,
    selected: boolean,
    opts?: {
      isMultiSelect?: boolean;
    }
  ) => void;
  studioClipCompleteClasses: ReturnType<typeof useStudioClipCompleteStyles>;
  children?: React.ReactElement<typeof StudioClipActions>;
  hideAvatar?: boolean;
  hideClipBodyTop?: boolean;
  hideBorder?: boolean;
  handleSave?: ({
    clip,
    editorText,
    setIsClipSaved,
  }: {
    clip: ClipObject;
    editorText: string;
    setIsClipSaved: (isSaved: boolean) => void;
  }) => void;
  playbackIconFilled?: boolean;
}

/**
 * StudioClip is a visual representation of a user 'clip'. A clip may be in one of many states
 * including a downloading, error, or playback state. Additionally, this component may be composed
 * with action items via the `StudioClipActions` child (see @example).
 *
 * @example
 * <StudioClip clip={clip}>
 *   <StudioClipActions>
 *     <StudioClipActionDelete ... />
 *     <StudioClipActionDownload ... />
 *     ...
 *   </StudioClipActions>
 * </StudioClip>
 */
const StudioClip = ({
  clip,
  draggableProvided,
  onNameChange,
  selected,
  onSelectedChange,
  studioClipCompleteClasses,
  children,
  isReadOnly,
  hideAvatar,
  hideClipBodyTop,
  hideBorder,
  previewClip,
  handleSave,
  playbackIconFilled,
}: StudioClipProps) => {
  const breakpoint = useBreakpoint();
  const { search } = usePersistentClipStateAndActions();
  const [focused, setFocused] = useState<boolean>(false);
  const [actionMenuAnchor, setActionMenuAnchor] =
    React.useState<null | HTMLElement>(null);
  const [srcFailedToLoad, setSrcFailedToLoad] = useState<boolean>();
  const [audio, setAudio] = useState<HTMLAudioElement | undefined>();
  const isComplete = instanceOfCompletedClip(clip);
  const isFailed = instanceOfFailedPendingClip(clip);
  const isDownloading = !isComplete && !isFailed;
  const studioClipContextValue = useMemo(
    () => ({
      clip: previewClip || clip,
      breakpoint,
      actionMenuAnchor,
      audio,
      setActionMenuAnchor,
      srcFailedToLoad,
      setSrcFailedToLoad,
      setAudio: (audioToSet: HTMLAudioElement | undefined) =>
        setAudio(audioToSet),
    }),
    [
      clip,
      previewClip,
      breakpoint,
      actionMenuAnchor,
      audio,
      setActionMenuAnchor,
      srcFailedToLoad,
      setSrcFailedToLoad,
      setAudio,
    ]
  );

  const handleSelected = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      if (typeof onSelectedChange === 'function')
        onSelectedChange(clip.id, !selected, { isMultiSelect: event.shiftKey });
    },
    [clip.id, selected, onSelectedChange]
  );

  return (
    <StudioClipContext.Provider value={studioClipContextValue}>
      <StudioClipCard
        selected={selected || false}
        focused={focused}
        hideBorder={hideBorder}
      >
        {isDownloading ? (
          <RenderingCardState />
        ) : (
          <>
            {draggableProvided && (
              <Hidden smDown implementation="css">
                <div
                  style={{
                    marginLeft: -8,
                    visibility: search ? 'hidden' : 'visible',
                  }}
                >
                  <Button
                    variant="icon"
                    aria-label="clip sorting drag handle"
                    className={
                      selected
                        ? studioClipCompleteClasses.classes.clipSelected
                        : studioClipCompleteClasses.classes.clipDeselected
                    }
                    {...draggableProvided.dragHandleProps}
                  >
                    <Icon type="dragHandle" />
                  </Button>
                </div>
              </Hidden>
            )}
            {onSelectedChange && !isReadOnly && (
              <Hidden smDown>
                <CheckboxInput
                  id={`clip-select-input-${clip.id}`}
                  aria-label={`Select clip ${clip.name}`}
                  value={clip.id}
                  checked={selected === true}
                  disabled={!!clip.failedWithError}
                  onClick={handleSelected}
                  className={clsx(
                    selected
                      ? studioClipCompleteClasses.classes.clipSelected
                      : studioClipCompleteClasses.classes.clipDeselected,
                    studioClipCompleteClasses.classes.checkbox
                  )}
                />
              </Hidden>
            )}
            <CompleteCardState
              selected={selected}
              setFocusedState={setFocused}
              onNameChange={onNameChange}
              studioClipCompleteClasses={studioClipCompleteClasses}
              hideAvatar={hideAvatar}
              hideClipBodyTop={hideClipBodyTop}
              previewClip={previewClip}
              handleSave={handleSave}
              playbackIconFilled={playbackIconFilled}
            >
              {children}
            </CompleteCardState>
          </>
        )}
      </StudioClipCard>
    </StudioClipContext.Provider>
  );
};

export default memo(StudioClip, (prevProps, nextProps) => {
  // Intended to prevent re-rendering while dragging!
  if (prevProps.isDragging && nextProps.isDragging) return true;
  return shallowCompare(prevProps, nextProps);
});
